import React from 'react';
import {
  Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select,
  TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import moment from 'moment';
import RequestManager from '../utilities/requestManager';
import { StateManager } from '../utilities/stateManager';
import { StackedDialog, useStackZIndex, useSelectMenuProps } from '../utilities/ModalStackContext';
import InPersonSignDialog from './CarModal/InPersonSignDialog';

const devtype = process.env.NODE_ENV === 'development' ? 'dev' : 'prod';

// The affidavit hangs off either parent. Only the docType differs; everything
// else on the request is the same, because the backend reads the customer and
// the vehicle from whichever record the docType points at.
const DOC_TYPES = { car: 'cc_auth', serviceOrder: 'cc_auth_service' };

const brandLabel = (brand) => (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card');
const expString = (m, y) => `${String(m).padStart(2, '0')}/${String(y).slice(-2)}`;

const signerName = (c = {}) =>
  c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '';

/**
 * Affidavit of Authorization for Credit Card Charges.
 *
 * The customer swears the card is theirs, authorizes one exact charge, and
 * waives a chargeback on it — so the amount, the date and the card have to be
 * the ones actually being run. Staff confirm all three here before the document
 * is built; the customer's name, address and vehicle come from Firestore.
 *
 * Card last four and expiration are not in Firestore. They come from Stripe via
 * the same `listPaymentMethods` call SavedCardsList makes, with manual entry as
 * the fallback for a card that is not on file.
 */
export default function CreditCardAffidavitDialog({
  open, onClose, parentType = 'car', refId, customer = {}, defaultAmount = 0, vehicleLabel = '',
}) {
  // This component both creates the dialog and holds the Select, so the stack
  // context has not been pushed yet at this point — pass the z explicitly.
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const docType = DOC_TYPES[parentType];

  const [amount, setAmount] = React.useState('');
  const [chargeDate, setChargeDate] = React.useState('');
  const [cardId, setCardId] = React.useState('');
  const [last4, setLast4] = React.useState('');
  const [exp, setExp] = React.useState('');
  const [cards, setCards] = React.useState([]);
  const [loadingCards, setLoadingCards] = React.useState(false);
  const [busy, setBusy] = React.useState('');

  const [inPersonOpen, setInPersonOpen] = React.useState(false);
  const [inPersonUrl, setInPersonUrl] = React.useState('');

  const name = signerName(customer);
  const email = customer.email || '';

  // Reset to the charge being collected each time the dialog opens, so a second
  // affidavit never inherits the first one's amount.
  React.useEffect(() => {
    if (!open) return;
    setAmount(defaultAmount > 0 ? Number(defaultAmount).toFixed(2) : '');
    setChargeDate(moment().format('YYYY-MM-DD'));
    setCardId('');
    setLast4('');
    setExp('');
  }, [open, defaultAmount]);

  React.useEffect(() => {
    if (!open || !customer.stripe_id) { setCards([]); return; }
    let cancelled = false;
    setLoadingCards(true);
    RequestManager.post({
      function: 'listPaymentMethods',
      variables: { params: { customer: customer.stripe_id, devtype } },
    }).then((res) => {
      if (cancelled) return;
      setLoadingCards(false);
      setCards(res && res.success ? (res.cards || []) : []);
    }).catch(() => { if (!cancelled) { setLoadingCards(false); setCards([]); } });
    return () => { cancelled = true; };
  }, [open, customer.stripe_id]);

  const pickCard = (id) => {
    setCardId(id);
    const card = cards.find(c => c.id === id);
    if (card) {
      setLast4(card.last4 || '');
      setExp(expString(card.exp_month, card.exp_year));
    }
  };

  const formData = () => ({
    cardLast4: last4.trim(),
    cardExp: exp.trim(),
    amount: parseFloat(amount) || 0,
    chargeDate,
  });

  const subject = `Credit Card Authorization - ${vehicleLabel || refId}`;

  const sessionVariables = (inPerson) => ({
    docType,
    refId,
    signers: [{ role: 'customer', email, name }],
    inPerson,
    formData: formData(),
    name: `${refId} Credit Card Authorization`,
    subject,
    message: 'Please review and sign the credit card authorization for your balance with Skyway Classics.',
    sentById: StateManager.userID,
  });

  const validate = ({ requireEmail }) => {
    if (!(parseFloat(amount) > 0)) {
      StateManager.setAlertAndOpen('Enter the amount being charged.', 'error');
      return false;
    }
    if (requireEmail && !email) {
      StateManager.setAlertAndOpen('This customer has no email address. Add one, or use Sign in Person.', 'error');
      return false;
    }
    return true;
  };

  const preview = async () => {
    if (!validate({ requireEmail: false })) return;
    setBusy('preview');
    const res = await RequestManager.post({
      function: 'previewFieldMap',
      variables: { docType, refId, formData: formData(), markers: false },
    });
    setBusy('');
    if (res && res.url) window.open(res.url, '_blank');
    else StateManager.setAlertAndOpen(res?.message || 'Could not build the preview.', 'error');
  };

  const send = async () => {
    if (!validate({ requireEmail: true })) return;
    setBusy('send');
    const res = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(false) });
    setBusy('');
    if (res && res.status === 'complete') {
      StateManager.setAlertAndOpen('Authorization sent for signature.', 'success');
      onClose();
    } else StateManager.setAlertAndOpen(res?.message || 'Could not send the authorization.', 'error');
  };

  const signHere = async () => {
    if (!validate({ requireEmail: false })) return;
    setBusy('inperson');
    const res = await RequestManager.post({ function: 'createSignatureSession', variables: sessionVariables(true) });
    setBusy('');
    if (res && res.status === 'complete') {
      setInPersonUrl(res.signUrl);
      setInPersonOpen(true);
    } else StateManager.setAlertAndOpen(res?.message || 'Could not start in-person signing.', 'error');
  };

  return (
    <>
      <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Credit Card Authorization
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {name || 'This customer'} signs an affidavit authorizing one exact charge and waiving a chargeback on it.
            Name, address and vehicle are read from the {parentType === 'serviceOrder' ? 'service order' : 'deal'}.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Amount to charge" type="number" required
                value={amount} onChange={e => setAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Charge date" type="date" required
                value={chargeDate} onChange={e => setChargeDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {!!cards.length && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Card on file</InputLabel>
                  <Select
                    label="Card on file" value={cardId} MenuProps={selectMenuProps}
                    onChange={e => pickCard(e.target.value)}
                  >
                    {cards.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {`${brandLabel(c.brand)} •••• ${c.last4} — exp ${expString(c.exp_month, c.exp_year)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {loadingCards && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Loading cards on file…</Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Card last 4"
                value={last4} onChange={e => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                helperText="Leave blank to print a blank line"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Expiration (MM/YY)"
                value={exp} onChange={e => setExp(e.target.value)}
                helperText="Leave blank to print a blank line"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Signer" value={email ? `${name} <${email}>` : name}
                InputProps={{ readOnly: true }}
                helperText={email ? 'Emailed a signing link.' : 'No email on file — use Sign in Person.'}
                error={!email}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={<PictureAsPdfOutlinedIcon />} onClick={preview} disabled={!!busy}
          >
            {busy === 'preview' ? 'Building…' : 'Preview'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="outlined" startIcon={<TabletMacOutlinedIcon />} onClick={signHere} disabled={!!busy}
          >
            Sign in Person
          </Button>
          <Button
            variant="contained" startIcon={<DrawOutlinedIcon />} onClick={send} disabled={!!busy || !email}
          >
            {busy === 'send' ? 'Sending…' : 'Send for Signature'}
          </Button>
        </DialogActions>
      </StackedDialog>

      <InPersonSignDialog
        open={inPersonOpen}
        onClose={() => { setInPersonOpen(false); onClose(); }}
        signUrl={inPersonUrl}
        signerName={name}
      />
    </>
  );
}
